import { Spin } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactFlow, {
  Controls,
  ReactFlowProvider,
  addEdge,
  removeElements,
  MiniMap,
} from 'react-flow-renderer';

import background from '../../svg/node/bg.svg';
import NodesProductDataPanel from './NodesProductDataPanel';
import Sidebar from './DnDSidebar';
import CustomNode from './Nodes/CustomNode';
import ExportNode from './Nodes/ExportNode';
import FeedNode from './Nodes/FeedNode';
import FilterNode from './Nodes/FilterNode';
import GraphicNode from './Nodes/GraphicNode';
import { NoteNode } from './Nodes/NoteNode';
import TemplatesSideBar from './components/TemplatesSideBar';
import { COLORS } from '../../constants';
import useNodesProjectStore from './stores/useNodesProjectStore';
import { useNodesProjectSelectors } from './hooks/useNodesProjectSelectors';
import { useNodesFlowSelectors } from './hooks/useNodesFlowSelectors';
import { useNodesFeedUiSelectors } from './hooks/useNodesFeedUiSelectors';

import styles from './Dnd.module.css';

const sortNodesByType = (arr) => {
  return arr.sort((a, b) => {
    if (a.type === 'note') return -1;
    if (b.type === 'note') return 1;
    return 0;
  });
};

const nodeTypes = {
  custom: CustomNode,
  feed: FeedNode,
  filter: FilterNode,
  graphic: GraphicNode,
  export: ExportNode,
  note: NoteNode,
};

const { mainViolet, orange, lightGreen, bluredBlue, bluredYellow, grayBackground } = COLORS;

const mapColors = {
  export: lightGreen,
  filter: orange,
  graphic: mainViolet,
  feed: bluredBlue,
  note: bluredYellow,
  default: grayBackground,
};

let id = 0;
const getId = () => `dndnode_${id++}`;

const DnDFlow = ({ loading }) => {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const { project, projectId, feedId, feedTypes, organization } = useNodesProjectSelectors();
  const {
    elements,
    setElements,
    rule,
    setRule,
    setNodesRf,
    downloadStage,
    loadProject,
    loadDesigns,
    loadTemplates,
    setRuleFetched,
    setProductFields,
  } = useNodesFlowSelectors();
  const {
    feedPage,
    designTemplates,
    isTemplatesSideBarOpen,
    isDataPreviewSideBarOpen,
    isAsideItemLoading,
    productIndex,
    feedPageIndex,
    run,
    loadFeed,
    showTemplatesSideBar,
    showDataPreviewSideBar,
    deleteDesignTemplate,
    setProduct,
  } = useNodesFeedUiSelectors();

  const onConnect = useCallback(
    (params) => setElements(addEdge(params, useNodesProjectStore.getState().elements)),
    [setElements],
  );

  const onElementsRemove = useCallback(
    (elementsToRemove) => {
      if (elementsToRemove.some((element) => element.type === 'feed')) return;
      setElements(removeElements(elementsToRemove, useNodesProjectStore.getState().elements));
    },
    [setElements],
  );

  const onLoad = useCallback(
    (_reactFlowInstance) => {
      setReactFlowInstance(_reactFlowInstance);
      setNodesRf(_reactFlowInstance);
    },
    [setNodesRf],
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      if (!type) {
        console.log('Empty node element type');
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const { project: proj, feedTypes: ft, downloadStage: ds } = useNodesProjectStore.getState();

      const newNode = {
        id: getId(),
        type,
        position,
        data: { project: proj, save: ds, feedTypes: ft },
      };

      const currentElements = useNodesProjectStore.getState().elements;
      if (newNode.type === 'note') {
        setElements([newNode, ...currentElements]);
      } else {
        setElements([...currentElements, newNode]);
      }
    },
    [reactFlowInstance, setElements],
  );

  useEffect(() => {
    if (!project) return;

    if (project.rule?.rule_json) {
      const parsed = JSON.parse(project.rule.rule_json);
      setRule({ position: parsed.position, zoom: parsed.zoom });

      let oldIds = {};
      id = 0;

      for (let el of parsed.elements) {
        if (el.type !== 'default') {
          el.data = {
            ...el.data,
            project,
            save: downloadStage,
            feedTypes,
          };
          oldIds[el.id] = `dndnode_${id}`;
          el.id = `dndnode_${id}`;
          id++;
        }
      }
      for (let el of parsed.elements) {
        if (el.type === 'default') {
          el.data = { ...el.data, project };
          el.id = `dndnode_${id}`;
          el.source = oldIds[el.source];
          el.target = oldIds[el.target];
          id++;
        }
        if (el.type === 'graphic') {
          el.data.loadProject = loadProject;
          el.data.loadDesigns = loadDesigns;
          el.data.loadTemplates = loadTemplates;
        }
      }

      const sortedNotes = sortNodesByType(parsed.elements.slice());
      setElements(sortedNotes);
      setRuleFetched();
    } else {
      setElements([
        {
          id: 'dndnode_0',
          type: 'feed',
          data: { project },
          position: { x: 250, y: 5 },
        },
      ]);
      id = 1;
    }

    if (project.feed?.feed_fields) {
      setProductFields(JSON.parse(project.feed.feed_fields));
    }
  }, [project]);

  useEffect(() => {
    if (!feedPage) return;
    const currentIndex = productIndex % feedPage.page_size;
    if (feedPage.products?.length && currentIndex < feedPage.products.length) {
      setProduct(feedPage.products[currentIndex]);
    }
  }, [feedPage, productIndex, setProduct]);

  useEffect(() => {
    loadFeed(feedPageIndex);
  }, [feedPageIndex, feedId, loadFeed]);

  const onSelectChange = useCallback(
    (selectedElements) => {
      const currentElements = useNodesProjectStore.getState().elements;
      const updated = currentElements.map((element) => {
        if (element.data) {
          return {
            ...element,
            data: {
              ...element.data,
              nodeSelected: selectedElements?.[0]?.id === element.id,
            },
          };
        }
        return element;
      });
      setElements(updated);
    },
    [setElements],
  );

  const getMiniMapNodesColor = useCallback(
    (type) => mapColors[type] ?? mapColors.default,
    [],
  );

  return (
    <div className={styles.dndflow}>
      <Sidebar
        save_func={downloadStage}
        run_func={run}
        showTemplatesSideBar={showTemplatesSideBar}
        isTemplatesSideBarOpen={isTemplatesSideBarOpen}
        showDataPreviewSideBar={showDataPreviewSideBar}
        isShowDataPreviewSideBar={isDataPreviewSideBarOpen}
        organization={organization}
        projectId={projectId}
        isAsideItemLoading={isAsideItemLoading}
        projectLoading={loading}
      />
      <ReactFlowProvider>
        {isTemplatesSideBarOpen && (
          <TemplatesSideBar
            onClose={showTemplatesSideBar}
            loadTemplates={loadTemplates}
            designTemplates={designTemplates}
            deleteTemplate={deleteDesignTemplate}
            projectId={projectId}
            save_func={downloadStage}
            loadProject={loadProject}
            organization={organization}
          />
        )}
        {isDataPreviewSideBarOpen && (
          <NodesProductDataPanel
            onClose={showDataPreviewSideBar}
          />
        )}
        <div
          className={styles.reactflowWrapper}
          ref={reactFlowWrapper}
          style={{ backgroundImage: `url(${background})` }}
        >
          {loading ? (
            <div className="loading-container">
              <Spin size="large" />
            </div>
          ) : (
            <ReactFlow
              defaultPosition={rule?.position}
              defaultZoom={rule?.zoom}
              minZoom={0.1}
              maxZoom={4}
              elements={elements}
              nodeTypes={nodeTypes}
              onConnect={onConnect}
              onElementsRemove={onElementsRemove}
              onLoad={onLoad}
              onDrop={onDrop}
              onDragOver={onDragOver}
              selectNodesOnDrag
              elementsSelectable
              onSelectionChange={onSelectChange}
            >
              <MiniMap
                nodeBorderRadius={10}
                nodeColor={(node) => getMiniMapNodesColor(node.type)}
                className={styles.miniMap}
              />
              <Controls />
            </ReactFlow>
          )}
        </div>
      </ReactFlowProvider>
    </div>
  );
};

export default DnDFlow;
